import { getLocales } from 'expo-localization';

import { getEarlyIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getAllFlags } from 'state/launchdarkly/platform-client';
import { AppTakeoverLDFlag } from 'state/launchdarkly/variations';
import { region } from 'utils/environment';
import { Region } from 'utils/environment/types';

import { elevation } from './elevation/elevation';
import { mandalorian } from './mandalorian/mandalorian';
import { spongebob } from './spongebob/spongebob';
import { AppTakeoverConfig, TakeoverMenuSectionConfig, TakeoverPdpItemConfig } from './types';

export type AppTakeoverType = AppTakeoverConfig;

export type GetAppTakeoverResult = Partial<
  AppTakeoverConfig & { isTakeoverActive: boolean; name: string }
>;

// add additional takeovers here
const appTakeoverConfigs: Record<string, AppTakeoverConfig> = {
  spongebob,
  elevation,
  mandalorian,
};

const INACTIVE = Object.freeze({ isTakeoverActive: false as const });

/** get a takeover if one is active per LD date ranges */
export const useAppTakeover = (): GetAppTakeoverResult => {
  const appTakeover = useFlag(LaunchDarklyFlag.APP_TAKEOVER);
  return isAppTakeoverEnabledCore(appTakeover as AppTakeoverLDFlag);
};

/** OUTSIDE OF LD PROVIDER ONLY - get a takeover if one is active per LD date ranges */
export const getAppTakeover = () => {
  const appTakeover = getAllFlags()?.[LaunchDarklyFlag.APP_TAKEOVER] as AppTakeoverLDFlag;
  return isAppTakeoverEnabledCore(appTakeover as AppTakeoverLDFlag);
};

const isAppTakeoverEnabledCore = (appTakeover: AppTakeoverLDFlag): GetAppTakeoverResult => {
  if (getEarlyIsDesktopWebBreakpoint()) {
    // disable on desktop, designs assume mobile as is most of our users
    return INACTIVE;
  }

  if (getIsDisabledLanguage()) {
    return INACTIVE;
  }

  if (!appTakeover || !appTakeover?.length) {
    return INACTIVE;
  }

  const now = new Date();
  const activeTakeover = appTakeover.find(takeover => {
    const start = new Date(takeover.startDate);
    const end = new Date(takeover.endDate);
    return now >= start && now <= end;
  });
  if (activeTakeover) {
    return {
      ...appTakeoverConfigs[activeTakeover.name],
      isTakeoverActive: true,
      name: activeTakeover.name,
    };
  }
  return INACTIVE;
};

/** return a SHP config if takeover is active for the passed home page */
export const useAppTakeoverSimpleHomePage = (
  homePageId: string
): Partial<AppTakeoverType['homePage'] & { isTakeoverActive: boolean; fontFamily: string }> => {
  const appTakeover = useAppTakeover();

  if (!homePageId) {
    return { isTakeoverActive: false };
  }

  if (appTakeover?.homePage?.targetSimpleHomePageIds?.includes(homePageId)) {
    return {
      ...appTakeover?.homePage,
      isTakeoverActive: true,
      fontFamily: appTakeover?.fontFamily,
    };
  }
  return INACTIVE;
};

type MenuSectionResult = TakeoverMenuSectionConfig & {
  isTakeoverActive: boolean;
};

/** returns a menu section config if takeover is active for the passed section */
export const useAppTakeoverMenuSection = (sectionId: string): MenuSectionResult => {
  const appTakeover = useAppTakeover();

  if (!sectionId) {
    return INACTIVE;
  }

  const takeoverSectionConfig = appTakeover?.menu?.targetSectionIds[sectionId];
  if (takeoverSectionConfig && Object.keys(takeoverSectionConfig).length > 0) {
    return { ...takeoverSectionConfig, isTakeoverActive: true };
  }

  return INACTIVE;
};

export type PdpResult = Partial<
  AppTakeoverType['pdp'] & { isTakeoverActive: boolean; fontFamily: string } & TakeoverPdpItemConfig
>;

const getAppTakeoverPdpResult = (
  appTakeover: GetAppTakeoverResult,
  itemConfig?: TakeoverPdpItemConfig
): PdpResult => {
  if (!itemConfig) {
    return INACTIVE;
  }

  return {
    ...itemConfig,
    ...appTakeover?.pdp,
    isTakeoverActive: true,
    fontFamily: appTakeover?.fontFamily,
  };
};

export const getAppTakeoverPdpFromUrl = (
  appTakeover: GetAppTakeoverResult,
  itemUrl: string
): PdpResult => {
  if (!itemUrl) {
    return INACTIVE;
  }

  const itemConfig = Object.entries(appTakeover?.pdp?.targetMenuItemIds || {}).find(([id]) =>
    itemUrl.includes(id)
  )?.[1];

  return getAppTakeoverPdpResult(appTakeover, itemConfig);
};

export const useAppTakeoverCartItem = (itemUrl: string): PdpResult => {
  const appTakeover = useAppTakeover();
  return getAppTakeoverPdpFromUrl(appTakeover, itemUrl);
};

/** returns a PDP config if takeover is active for the menu item */
export const useAppTakeoverPdp = (itemId: string): PdpResult => {
  const appTakeover = useAppTakeover();

  if (!itemId) {
    return INACTIVE;
  }

  const itemConfig = appTakeover?.pdp?.targetMenuItemIds?.[itemId];
  return getAppTakeoverPdpResult(appTakeover, itemConfig);
};

const getIsDisabledLanguage = () => {
  if (region === Region.US) {
    return false;
  }

  try {
    const deviceLanguage = getLocales()?.[0]?.languageCode?.toLowerCase();
    if (deviceLanguage?.includes('fr')) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
};
